import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { 
  LayoutDashboard, Package, ShoppingBag, BarChart3, Settings, LogOut, Menu, X,
  ShoppingCart, Tag, Star, Video, Megaphone, FolderTree, Sparkles, BookOpen, Image
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { Logo } from './Logo';

function AdminLayout({ children }) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Hide main site header when admin layout is active
  useEffect(() => {
    const mainHeader = document.querySelector('header');
    if (mainHeader) {
      mainHeader.style.display = 'none';
    }
    
    return () => {
      if (mainHeader) {
        mainHeader.style.display = '';
      }
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    window.location.href = '/admin';
  };

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/products', icon: ShoppingBag, label: 'Products' },
    { path: '/admin/categories', icon: FolderTree, label: 'Categories' },
    { path: '/admin/banners', icon: Image, label: 'Hero Banners' },
    { path: '/admin/bundles', icon: Package, label: 'Bundles' },
    { path: '/admin/orders', icon: ShoppingCart, label: 'Orders' },
    { path: '/admin/blogs', icon: BookOpen, label: 'Blogs' },
    { path: '/admin/coupons', icon: Tag, label: 'Coupons' },
    { path: '/admin/reviews', icon: Star, label: 'Reviews' },
    { path: '/admin/videos', icon: Video, label: 'Videos' },
    { path: '/admin/marketing', icon: Megaphone, label: 'Marketing' },
    { path: '/admin/ai-content', icon: Sparkles, label: 'AI Content' },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="md:hidden bg-white border-b fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-2">
            <Logo size="small" />
            <span className="text-sm text-muted-foreground">Admin</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-white pt-16 flex flex-col">
          <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-cyan-50 text-cyan-600 font-medium'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
          <div className="p-4 border-t flex-shrink-0">
            <Button
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleLogout}
            >
              <LogOut className="w-5 h-5 mr-3" />
              Logout
            </Button>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className="hidden md:flex">
        <aside className="w-64 bg-white border-r min-h-screen fixed left-0 top-0 flex flex-col">
          <div className="p-6 border-b flex-shrink-0">
            <Logo size="default" />
            <p className="text-sm text-muted-foreground mt-2">Admin Panel</p>
          </div>
          
          <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-cyan-50 text-cyan-600 font-medium'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t flex-shrink-0">
            <Button
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleLogout}
            >
              <LogOut className="w-5 h-5 mr-3" />
              Logout
            </Button>
          </div>
        </aside>

        {/* Main Content - Desktop */}
        <main className="ml-64 flex-1 p-3 sm:p-4">
          {children}
        </main>
      </div>

      {/* Mobile Content */}
      <div className="md:hidden pt-16 p-3">
        {children}
      </div>
    </div>
  );
}

export { AdminLayout };
